import { useIntl } from 'react-intl';

import { BulbIcon } from '@/components/icons/bulb';
import { LightningBoltIcon } from '@/components/icons/lightning-bolt';

export type RecommendationType = 'sourcing' | 'automation';

export const useRecommendationContent = () => {
  const intl = useIntl();

  const getRecommendationContent = (
    type: RecommendationType,
    value?: number,
  ) => {
    switch (type) {
      case 'sourcing': {
        return {
          icon: BulbIcon,
          headerText: intl.formatMessage({
            defaultMessage: 'Start Sourcing',
            description:
              'Card section header: prompts user to set up job board campaigns',
          }),
          title: intl.formatMessage(
            {
              defaultMessage:
                '{count, plural, one {# live opening not being promoted} other {# live openings not being promoted}}',
              description:
                'Headline: count of active openings with no campaigns (plural form)',
            },
            { count: value ?? 0 },
          ),
          description: intl.formatMessage({
            defaultMessage:
              "These openings are active but don't have campaigns running yet.",
            description:
              'Supporting sentence under the count: why this recommendation appears',
          }),
          buttonText: intl.formatMessage({
            defaultMessage: 'Setup sourcing',
            description:
              'Primary CTA on the sourcing insight card to configure campaigns',
          }),
        };
      }
      case 'automation': {
        return {
          icon: LightningBoltIcon,
          headerText: intl.formatMessage({
            defaultMessage: 'Automation Recommendations',
            description:
              'Card section header: automation cost-saving opportunities',
          }),
          title: intl.formatMessage({
            defaultMessage: 'Save money automatically',
            description:
              'Value proposition line for budget automation on the card',
          }),
          description: intl.formatMessage({
            defaultMessage:
              'Automations pause campaigns when goals, budgets, or timelines are met.',
            description:
              'One-line explainer of what automations do for spend control',
          }),
          buttonText: intl.formatMessage({
            defaultMessage: 'Enable automations',
            description: 'CTA to turn on or manage sourcing automation rules',
          }),
        };
      }
      default: {
        return null;
      }
    }
  };

  return { getRecommendationContent };
};
